import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { PageHeader, Panel, PanelHeader } from '../../components/ui/Panel';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import Pill from '../../components/ui/Pill';
import { EmptyState, ErrorNotice, SkeletonRows } from '../../components/ui/States';
import { HeadRow, Td, Th, inputClass } from '../../components/review/parts';
import { CycleSummary, errorMessage, staffApi, staffBase, stateLabel } from '../../services/reviewApi';
import { canMutate } from '../../lib/roles';
import { useAuth } from '../../contexts/AuthContext';
import { fmt } from '../../lib/format';

export default function FellowReview() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [creating, setCreating] = useState(false);
  const [year, setYear] = useState(String(new Date().getFullYear() + 1));
  const cycles = useQuery<CycleSummary[]>(
    'review-cycles',
    async () => (await staffApi.get(`${staffBase}/cycles`)).data.data,
    { enabled: canMutate(user?.role) },
  );
  const create = useMutation(
    async () => (await staffApi.post(`${staffBase}/cycles`, { year: Number(year) })).data.data,
    {
      onSuccess: (data: { id: string }) => {
        queryClient.invalidateQueries('review-cycles');
        navigate(`/fellow-review/${data.id}`);
      },
    },
  );

  if (!canMutate(user?.role)) {
    return (
      <div>
        <PageHeader title="Fellow review" />
        <EmptyState title="Not available">
          Fellow application review is run by admin operators who are a cycle’s Program Manager.
        </EmptyState>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Fellow review"
        sub="One cycle per selection year: eligibility, reviewer assignment, scoring, calibration, deliberation and decisions. Only a cycle's Program Manager can open it; reviewers sign in to their own portal."
        aside={
          <Button variant="primary" onClick={() => setCreating(true)}>
            New cycle
          </Button>
        }
      />
      <Panel>
        <PanelHeader title="Cycles" note={cycles.data ? fmt(cycles.data.length) : undefined} />
        {cycles.isLoading ? (
          <SkeletonRows rows={3} />
        ) : cycles.error ? (
          <div className="p-5">
            <ErrorNotice>{errorMessage(cycles.error, 'Could not load the cycles.')}</ErrorNotice>
          </div>
        ) : !cycles.data?.length ? (
          <EmptyState title="No cycles yet">Create the first one with New cycle.</EmptyState>
        ) : (
          <table className="w-full">
            <thead>
              <HeadRow>
                <Th>Year</Th>
                <Th>Name</Th>
                <Th>State</Th>
                <Th>Applications</Th>
                <Th>Eligible</Th>
                <Th>Your seat</Th>
              </HeadRow>
            </thead>
            <tbody>
              {cycles.data.map((c) => (
                <tr
                  key={c.id}
                  className={`border-b border-line ${c.isProgramManager ? 'cursor-pointer hover:bg-selected' : ''}`}
                  onClick={() => c.isProgramManager && navigate(`/fellow-review/${c.id}`)}
                >
                  <Td className="tabular font-semibold">{c.year}</Td>
                  <Td>{c.name}</Td>
                  <Td>
                    <Pill tone={c.state === 'archived' ? 'inactive' : 'active'}>{stateLabel(c.state)}</Pill>
                  </Td>
                  <Td className="tabular">{fmt(c.applications)}</Td>
                  <Td className="tabular">{fmt(c.eligible)}</Td>
                  <Td>
                    {c.isProgramManager ? (
                      'Program Manager'
                    ) : (
                      <span className="text-ink-2">none — ask its Program Manager</span>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
      {creating ? (
        <Modal
          title="New cycle"
          onClose={() => setCreating(false)}
          footer={
            <div className="flex justify-end gap-3 w-full">
              <Button variant="ghost" onClick={() => setCreating(false)}>
                Cancel
              </Button>
              <Button variant="primary" disabled={create.isLoading} onClick={() => create.mutate()}>
                Create
              </Button>
            </div>
          }
        >
          <label className="block text-small text-ink-2 mb-1">Selection year</label>
          <input className={inputClass} value={year} onChange={(e) => setYear(e.target.value)} inputMode="numeric" />
          <p className="text-small text-ink-2 mt-2">
            The cycle starts in draft with the default rubric, eligibility rules and settings, and uses that year’s
            Fellow application form if it exists. You become its Program Manager.
          </p>
          {create.error ? (
            <div className="mt-3">
              <ErrorNotice>{errorMessage(create.error, 'Could not create the cycle.')}</ErrorNotice>
            </div>
          ) : null}
        </Modal>
      ) : null}
    </div>
  );
}
